import { v4 as uuidv4 } from "uuid";
import { LoremIpsum } from "lorem-ipsum";

import type {
	IMessageEntity,
	IStickerMessageEntity,
	ITextMessageEntity,
} from "../../shared/entities";
import { TextMessageEntityClass } from "../../domain/entites/text-message";
import { StickerMessageEntityClass } from "../../domain/entites/sticker-message";
import type { IMessageRepository } from "./message-repository.interface";

const lorem = new LoremIpsum({
	sentencesPerParagraph: {
		max: 8,
		min: 2,
	},
	wordsPerSentence: {
		max: 16,
		min: 4,
	},
});

class MessageRepository implements IMessageRepository {
	private messagesMap_: Map<string, IMessageEntity[]>;
	private isInitialized_: boolean = false;

	constructor() {
		this.messagesMap_ = new Map();
	}

	public async loadMessages(convId: string, opts?: any): Promise<IMessageEntity[]> {
		await this.setupMessages_();
		return this.messagesMap_.get(convId) ?? [];
	}

	public async saveMessage(message: IMessageEntity): Promise<IMessageEntity> {
		throw new Error("Not implemented yet!");
	}

	public async saveNewTextMessage(message: ITextMessageEntity): Promise<ITextMessageEntity> {
		const convId = message.toUid;
		const messages = this.messagesMap_.get(convId) ?? [];
		messages.push(message);
		this.messagesMap_.set(convId, messages);
		return message;
	}

	public async saveNewStickerMessage(
		message: IStickerMessageEntity,
	): Promise<IStickerMessageEntity> {
		const convId = message.toUid;
		const messages = this.messagesMap_.get(convId) ?? [];
		messages.push(message);
		this.messagesMap_.set(convId, messages);
		return message;
	}

	private async setupMessages_(): Promise<void> {
		if (!this.isInitialized_) {
			const messages = await this.generateMessageEntities_(100);
			this.messagesMap_.set("maihoangdiepanh", [...messages]);
			this.messagesMap_.set("maitrungdong", [...messages]);
			this.isInitialized_ = true;
		}
	}

	private async generateMessageEntities_(n: number = 100): Promise<IMessageEntity[]> {
		const result: IMessageEntity[] = [];
		for (let i = 1; i <= n; i++) {
			await new Promise((resolve) => setTimeout(resolve, 10));
			const shouldSpawnSticker = Math.random() > 0.65;
			if (shouldSpawnSticker) {
				const stickerMessageEntity = this.generateStickerMessageEntity_();
				result.push(stickerMessageEntity);
			} else {
				const textMessageEntity = this.generateTextMessageEntity_();
				textMessageEntity.content = `[#${i}]: ${textMessageEntity.content}`;
				result.push(textMessageEntity);
			}
		}
		return result;
	}

	private generateTextMessageEntity_(): ITextMessageEntity {
		const timestamp = Date.now();
		const textMessageEntity = new TextMessageEntityClass(
			"text",
			lorem.generateParagraphs(1),
			uuidv4(),
			timestamp.toString(),
			Math.random() > 0.5 ? "maihoangdiepanh" : "maitrungdong",
			Math.random() > 0.5 ? "maitrungdong" : "maihoangdiepanh",
			timestamp,
		);
		textMessageEntity.updateTtl(-1);
		textMessageEntity.updateLocalDttm(timestamp);
		textMessageEntity.updateServerTime(timestamp);
		textMessageEntity.updateE2eeStatus(-1);
		textMessageEntity.updateUrgency(null);

		return textMessageEntity.toPlainObject();
	}

	private generateStickerMessageEntity_(): IStickerMessageEntity {
		const timestamp = Date.now();
		const stickerMessageEntity = new StickerMessageEntityClass(
			"sticker",
			{
				id: 23037,
				cateId: 10398,
				type: 7,
				text: "[^10398.23037^]",
				uri: "23037",
				fkey: 0,
				status: 0,
				stickerUrl:
					"https://zalo-api.zadn.vn/api/emoticon/sticker/webpc?eid=23037&size=130",
				stickerSpriteUrl: "https://zalo-api.zadn.vn/api/emoticon/sprite?eid=23037&size=130",
				stickerWebpUrl: null,
				totalFrames: 0,
				duration: 300,
				effectId: 0,
				checksum: "",
				ext: 0,
				source: 0,
				fss: null,
				fssInfo: null,
				version: 0,
				extInfo: null,
				fetchTs: 1745311325313,
				updateState: 0,
			},
			uuidv4(),
			timestamp.toString(),
			Math.random() > 0.5 ? "maihoangdiepanh" : "maitrungdong",
			Math.random() > 0.5 ? "maitrungdong" : "maihoangdiepanh",
			timestamp,
		);
		stickerMessageEntity.updateTtl(-1);
		stickerMessageEntity.updateLocalDttm(timestamp);
		stickerMessageEntity.updateServerTime(timestamp);
		stickerMessageEntity.updateE2eeStatus(-1);
		stickerMessageEntity.updateUrgency(null);

		return stickerMessageEntity.toPlainObject();
	}

	public async deleteMessagesByCliMsgIds(
		convId: string,
		cliMsgIds: string[],
	): Promise<IMessageEntity[]> {
		const messages = this.messagesMap_.get(convId) ?? [];
		const newMessages = messages.filter((message) => !cliMsgIds.includes(message.cliMsgId));
		this.messagesMap_.set(convId, newMessages);
		return newMessages;
	}
}

const messageRepository = new MessageRepository();
export default messageRepository;
